// Shared status mapping module for MEBI UI
// This centralizes status labels, colors and badge variants for CategoryCard and TournamentCard.

export type BadgeColor =
  | "green"
  | "blue"
  | "yellow"
  | "orange"
  | "red"
  | "purple"
  | "teal"
  | "violet"
  | "gray";
export type BadgeVariant = "filled" | "outlined" | "subtle";

export interface StatusConfig {
  label: string;
  badgeColor: BadgeColor;
  variant: BadgeVariant;
}

export type StatusType =
  | "category"
  | "tournament"
  | "registration"
  | "match"
  | "match_result"
  | "checkin"
  | "organizer";

// Tournament status mappings (aligned with Badge.tsx)
export const TOURNAMENT_STATUS: Record<string, StatusConfig> = {
  draft: { label: "Nháp", badgeColor: "purple", variant: "subtle" },
  planned: { label: "Đã lên lịch", badgeColor: "purple", variant: "filled" },
  registration_open: {
    label: "Mở đăng ký",
    badgeColor: "green",
    variant: "subtle",
  },
  registration_full: {
    label: "Đủ người",
    badgeColor: "violet",
    variant: "subtle",
  },
  registration_expired: {
    label: "Hết hạn đăng ký",
    badgeColor: "orange",
    variant: "subtle",
  },
  registration_closed: {
    label: "Đóng đăng ký",
    badgeColor: "red",
    variant: "subtle",
  },
  upcoming: { label: "Sắp diễn ra", badgeColor: "yellow", variant: "subtle" },
  ongoing: { label: "Đang diễn ra", badgeColor: "teal", variant: "subtle" },
  postponed: { label: "Bị hoãn", badgeColor: "violet", variant: "subtle" },
  cancelled: { label: "Bị hủy", badgeColor: "red", variant: "filled" },
  completed: { label: "Đã kết thúc", badgeColor: "blue", variant: "subtle" },
};

// Category status mappings (visual labels follow the Figma spec shown)
// Colors/variants leverage the same token set as Tournament status
export const CATEGORY_STATUS: Record<string, StatusConfig> = {
  draft: { label: "Nháp", badgeColor: "purple", variant: "subtle" },
  planned: { label: "Đã lên lịch", badgeColor: "purple", variant: "filled" },
  registration_open: {
    label: "Mở đăng ký",
    badgeColor: "green",
    variant: "subtle",
  },
  registration_full: {
    label: "Đủ người",
    badgeColor: "violet",
    variant: "subtle",
  },
  registration_expired: {
    label: "Hết hạn đăng ký",
    badgeColor: "orange",
    variant: "subtle",
  },
  registration_closed: {
    label: "Đóng đăng ký",
    badgeColor: "red",
    variant: "subtle",
  },
  upcoming: { label: "Sắp diễn ra", badgeColor: "yellow", variant: "subtle" },
  ongoing: { label: "Đang diễn ra", badgeColor: "teal", variant: "subtle" },
  postponed: { label: "Bị hoãn", badgeColor: "purple", variant: "subtle" },
  cancelled: { label: "Bị hủy", badgeColor: "red", variant: "filled" },
  completed: { label: "Đã kết thúc", badgeColor: "blue", variant: "subtle" },
};

export const REGISTRATION_STATUS: Record<string, StatusConfig> = {
  pending_approval: {
    label: "Chờ duyệt",
    badgeColor: "blue",
    variant: "outlined",
  },
  profile_approved: {
    label: "Đã duyệt hồ sơ",
    badgeColor: "green",
    variant: "outlined",
  },
  payment_pending: {
    label: "Chưa đóng phí",
    badgeColor: "orange",
    variant: "outlined",
  },
  payment_confirmed: {
    label: "Đã xác nhận phí",
    badgeColor: "green",
    variant: "filled",
  },
  rejected: {
    label: "Từ chối",
    badgeColor: "red",
    variant: "filled",
  },
  user_cancelled: {
    label: "Hủy đăng ký",
    badgeColor: "red",
    variant: "outlined",
  },
  organizer_cancelled: {
    label: "Organizer hủy đăng ký",
    badgeColor: "red",
    variant: "filled",
  },
};

export const MATCH_STATUS: Record<string, StatusConfig> = {
  not_started: {
    label: "Chưa diễn ra",
    badgeColor: "gray",
    variant: "outlined",
  },
  upcoming: {
    label: "Sắp diễn ra",
    badgeColor: "blue",
    variant: "filled",
  },
  waiting_checkin: {
    label: "Chờ check-in",
    badgeColor: "yellow",
    variant: "outlined",
  },
  absent: {
    label: "Vắng mặt",
    badgeColor: "red",
    variant: "outlined",
  },
  in_progress: {
    label: "Đang diễn ra",
    badgeColor: "green",
    variant: "filled",
  },
  completed: {
    label: "Đã kết thúc",
    badgeColor: "gray",
    variant: "filled",
  },
  postponed: {
    label: "Bị hoãn",
    badgeColor: "yellow",
    variant: "filled",
  },
  cancelled: {
    label: "Đã huỷ",
    badgeColor: "red",
    variant: "filled",
  },
};

export const MATCH_RESULT_STATUS: Record<string, StatusConfig> = {
  no_result: {
    label: "Chưa có kết quả",
    badgeColor: "blue",
    variant: "outlined",
  },
  win: {
    label: "Thắng",
    badgeColor: "green",
    variant: "filled",
  },
  draw: {
    label: "Hòa",
    badgeColor: "yellow",
    variant: "filled",
  },
  loss: {
    label: "Thua",
    badgeColor: "red",
    variant: "filled",
  },
};

export const CHECKIN_STATUS: Record<string, StatusConfig> = {
  not_checked_in: {
    label: "Chưa check-in",
    badgeColor: "orange",
    variant: "outlined",
  },
  checked_in: {
    label: "Đã check-in",
    badgeColor: "green",
    variant: "filled",
  },
  absent: {
    label: "Vắng mặt",
    badgeColor: "red",
    variant: "filled",
  },
};

export const ORGANIZER_TYPE_MAP: Record<string, StatusConfig> = {
  club: { label: "Câu lạc bộ", badgeColor: "teal", variant: "subtle" },
  business: { label: "Doanh nghiệp", badgeColor: "blue", variant: "subtle" },
  community: {
    label: "Đơn vị tổ chức giải đấu",
    badgeColor: "purple",
    variant: "subtle",
  },
  federation: { label: "Liên đoàn", badgeColor: "violet", variant: "filled" },
};

// Additional mappings can be added here to mirror Badge.tsx (registration, match, etc.) as needed

const ALIASES: Record<StatusType, Record<string, string>> = {
  category: {
    open: "registration_open",
    "mở đăng ký": "registration_open",
    "đang mở": "registration_open",
    full: "registration_full",
    "đủ người": "registration_full",
    closed: "registration_closed",
    "đóng đăng ký": "registration_closed",
    draft: "draft",
    nháp: "draft",
    planned: "planned",
    "đã lên lịch": "planned",
    upcoming: "upcoming",
    "sắp diễn ra": "upcoming",
    ongoing: "ongoing",
    "đang diễn ra": "ongoing",
    postponed: "postponed",
    delayed: "postponed",
    cancelled: "cancelled",
    "bị hủy": "cancelled",
    completed: "completed",
    "đã kết thúc": "completed",
  },
  tournament: {
    open: "registration_open",
    published: "registration_open",
    "mở đăng ký": "registration_open",
    "đang mở": "registration_open",
    full: "registration_full",
    "đủ người": "registration_full",
    expired: "registration_expired",
    "hết hạn": "registration_expired",
    closed: "registration_closed",
    "đóng đăng ký": "registration_closed",
    draft: "draft",
    nháp: "draft",
    planned: "planned",
    "đã lên lịch": "planned",
    upcoming: "upcoming",
    "sắp diễn ra": "upcoming",
    ongoing: "ongoing",
    "đang diễn ra": "ongoing",
    postponed: "postponed",
    delayed: "postponed",
    cancelled: "cancelled",
    "đã hủy": "cancelled",
    "bị hủy": "cancelled",
    completed: "completed",
    "đã kết thúc": "completed",
  },
  registration: {},
  match: {},
  match_result: {},
  checkin: {},
  organizer: {
    "câu lạc bộ": "club",
    "doanh nghiệp": "business",
    "đơn vị tổ chức giải đấu": "community",
    "liên đoàn": "federation",
  },
};

export const STATUS_MAP: Record<StatusType, Record<string, StatusConfig>> = {
  category: CATEGORY_STATUS,
  tournament: TOURNAMENT_STATUS,
  registration: REGISTRATION_STATUS,
  match: MATCH_STATUS,
  match_result: MATCH_RESULT_STATUS,
  checkin: CHECKIN_STATUS,
  organizer: ORGANIZER_TYPE_MAP,
};

export function normalizeStatusKey(
  type: StatusType,
  value?: string,
): string | undefined {
  if (!value) return undefined;
  const v = value.trim().toLowerCase();
  const alias = ALIASES[type][v];
  if (alias) return alias;
  // If the raw value matches a known key, return it
  if (STATUS_MAP[type][v]) return v;
  return undefined;
}

export function getBadgeProps(
  type: StatusType,
  value?: string,
): StatusConfig | undefined {
  const key = normalizeStatusKey(type, value);
  if (!key) return undefined;
  return STATUS_MAP[type][key];
}

export function resolveLabel(
  type: StatusType,
  value?: string,
): string | undefined {
  const p = getBadgeProps(type, value);
  return p?.label;
}

export function resolveBadgeColor(
  type: StatusType,
  value?: string,
): BadgeColor | undefined {
  const p = getBadgeProps(type, value);
  return p?.badgeColor;
}

export function resolveBadgeVariant(
  type: StatusType,
  value?: string,
): BadgeVariant | undefined {
  const p = getBadgeProps(type, value);
  return p?.variant;
}
